import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { api, isInsufficientCredits } from "../api";
import { BackIcon } from "../components/icons";
import { ProgressDialog } from "../components/ProgressDialog";
import { normalizeLanguage } from "../i18n";

const MIN_PAGES = 2;
const MAX_PAGES = 12;
const MAX_TITLE = 200;
const MAX_BRIEF = 1500;
const MAX_REFERENCES = 2;
const MAX_REF_CAPTION = 200;
const DEFAULT_LANGUAGES = ["English", "Chinese"];

// Map the active UI locale onto one of DEFAULT_LANGUAGES so the story defaults
// to the language the parent is already reading the app in (a zh-CN UI gets a
// Chinese story). Any UI locale without a mapping falls back to English.
const UI_TO_STORY_LANGUAGE: Record<string, string> = {
  en: "English",
  "zh-CN": "Chinese",
};

function defaultStoryLanguage(uiLang: string): string {
  return UI_TO_STORY_LANGUAGE[normalizeLanguage(uiLang)] ?? "English";
}

// Intentionally omit image/heic|heif — same reasoning as Compose.tsx: iOS
// Safari only auto-transcodes to JPEG when HEIC isn't an accepted type;
// listing it hands us raw HEIC bytes the <img>/background preview can't
// render, so the parent sees a silently-broken reference tile.
const REF_MIME = ["image/png", "image/jpeg", "image/webp"].join(",");

interface RefSlot {
  file: File | null;
  previewUrl: string | null;
  caption: string;
  stagedKey?: string;
  contentType?: string;
}

const EMPTY_SLOT: RefSlot = { file: null, previewUrl: null, caption: "" };

/**
 * Story-mode creation form. Submits once with title + brief + language +
 * page count (+ optional reference images); the server creates the book +
 * N empty page rows + enqueues the generation job in one shot, so we jump
 * straight to /books/:id (Processing).
 */
export default function StoryCompose() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const editState = (location.state as { prefill?: { title: string; brief: string; language: string; pageCount: number; references: Array<{ key: string; contentType: string; description: string | null; previewUrl: string }> }; revisionOf?: string } | null) ?? null;
  const prefill = editState?.prefill ?? null;
  const revisionOf = editState?.revisionOf ?? null;
  const [title, setTitle] = useState(prefill?.title ?? "");
  const [brief, setBrief] = useState(prefill?.brief ?? "");
  const [language, setLanguage] = useState(() => prefill?.language ?? defaultStoryLanguage(i18n.language));
  const [pageCount, setPageCount] = useState(prefill?.pageCount ?? 6);
  const [refs, setRefs] = useState<RefSlot[]>(() =>
    prefill?.references?.length
      ? prefill.references.map((r) => ({ file: null, previewUrl: r.previewUrl, caption: r.description ?? "", stagedKey: r.key, contentType: r.contentType }))
      : [EMPTY_SLOT, EMPTY_SLOT],
  );
  const [refsExpanded, setRefsExpanded] = useState(() => !!(prefill?.references?.length));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Fetch the live balance on mount so the cost summary stays accurate even
  // after the user made a book earlier this session. Null means "unknown"
  // (fetch pending/failed) — we never block submit in that case; the server
  // 402 stays the backstop.
  const [credits, setCredits] = useState<{ balance: number | null; unlimited: boolean; perPage: number } | null>(null);
  useEffect(() => {
    let alive = true;
    void api.me().then((m) => {
      if (alive && m) setCredits({ balance: m.credits?.balance ?? null, unlimited: m.credits?.unlimited ?? false, perPage: m.creditsPerPage ?? 10 });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const cost = pageCount * (credits?.perPage ?? 10);
  const balance = credits?.balance ?? 0;
  const cantAfford = credits != null && !credits.unlimited && cost > balance;

  const hasReferenceImages = refs.some((slot) => slot.file || slot.stagedKey);
  const canSubmit =
    !busy && brief.trim().length > 0 && pageCount >= MIN_PAGES && pageCount <= MAX_PAGES && !cantAfford;

  const updateSlot = (i: number, patch: Partial<RefSlot>) => {
    setRefs((prev) => {
      const next = prev.slice();
      const current = next[i] ?? EMPTY_SLOT;
      // Release the old object-URL when the file changes, to avoid a tiny leak.
      if (patch.file !== undefined && current.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }
      next[i] = { ...current, ...patch } as RefSlot;
      return next;
    });
  };

  const pickFile = (i: number, file: File | null) => {
    if (!file) {
      updateSlot(i, { file: null, previewUrl: null });
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    updateSlot(i, { file, previewUrl });
  };

  const clearSlot = (i: number) => {
    updateSlot(i, { file: null, previewUrl: null, caption: "", stagedKey: undefined, contentType: undefined });
  };

  const submit = async (asReplace: boolean) => {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      // Stage every populated reference slot in parallel — they're
      // independent uploads. Slot order is preserved so the server
      // receives them in the same order as the UI shows.
      // Slots with a stagedKey already have a server-side copy; skip re-upload.
      const stagedByIndex = await Promise.all(
        refs.map(async (slot, i) => {
          if (slot.stagedKey) return { index: i, key: slot.stagedKey, contentType: slot.contentType ?? "image/png", description: slot.caption.trim() || null };
          if (!slot.file) return null;
          const staged = await api.stageReferenceUpload(slot.file);
          return {
            index: i,
            key: staged.key,
            contentType: staged.contentType,
            description: slot.caption.trim() || null,
          };
        }),
      );
      const references = stagedByIndex
        .filter((s): s is NonNullable<typeof s> => s !== null)
        .slice(0, MAX_REFERENCES)
        .map(({ key, contentType, description }) => ({ key, contentType, description }));

      const { book } = await api.createStoryBook({
        title: title.trim(),
        brief: brief.trim(),
        language: language.trim() || "English",
        pageCount,
        ...(references.length > 0 ? { references } : {}),
        ...(revisionOf && asReplace ? { revisionOf, revisionIntent: "replace" as const } : {}),
      });
      navigate(`/app/books/${book.id}`, { replace: true });
    } catch (e) {
      setErr(isInsufficientCredits(e) ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="tp-app tp-wide">
      <div className="tp-topbar">
        <Link to="/app/new" className="tp-btn tp-btn-ghost" aria-label={t("common.back")} style={{ textDecoration: "none" }}>
          <BackIcon /> {t("common.back")}
        </Link>
      </div>
      <div className="tp-screen" style={{ paddingTop: 16 }}>
        <div className="tp-display" style={{ marginBottom: 8, fontSize: 32 }}>{t("storyCompose.title")}</div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 20, fontSize: 15 }}>
          {t("storyCompose.subtitle")}
        </div>

        {err && <div className="tp-error" style={{ marginBottom: 16 }}>{err}</div>}

        <label className="tp-field-label">{t("storyCompose.titleFieldLabel")}</label>
        <input
          className="tp-input"
          type="text"
          maxLength={MAX_TITLE}
          placeholder={t("storyCompose.titlePlaceholder")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={busy}
        />

        <label className="tp-field-label" style={{ marginTop: 16 }}>{t("storyCompose.briefFieldLabel")}</label>
        <textarea
          className="tp-input"
          rows={5}
          maxLength={MAX_BRIEF}
          placeholder={t("storyCompose.briefPlaceholder")}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          disabled={busy}
        />
        <div className="tp-eyebrow" style={{ marginTop: 4, color: "var(--tp-ink-soft)", fontSize: 11 }}>
          {brief.length}/{MAX_BRIEF}
        </div>

        {!refsExpanded ? (
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="tp-btn"
              onClick={() => setRefsExpanded(true)}
              disabled={busy}
              style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
            >
              <Trans
                i18nKey="storyCompose.addReferenceImages"
                components={{ optional: <span style={{ color: "var(--tp-ink-soft)", fontWeight: "normal" }} /> }}
              />
            </button>
            <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginTop: 6, fontSize: 12 }}>
              <Trans i18nKey="storyCompose.referenceHintCollapsed" components={{ em: <em /> }} />
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
              <label className="tp-field-label" style={{ margin: 0 }}>{t("storyCompose.referenceImagesLabel")}</label>
              <button
                type="button"
                onClick={() => {
                  refs.forEach((_, i) => clearSlot(i));
                  setRefsExpanded(false);
                }}
                disabled={busy}
                className="tp-btn tp-btn-ghost"
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                {t("storyCompose.hide")}
              </button>
            </div>
            <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 8, fontSize: 12 }}>
              {t("storyCompose.referenceHintExpanded")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {refs.map((slot, i) => (
                <ReferenceSlot
                  key={i}
                  index={i}
                  slot={slot}
                  disabled={busy}
                  onPick={(file) => pickFile(i, file)}
                  onCaption={(caption) => updateSlot(i, { caption })}
                  onClear={() => clearSlot(i)}
                />
              ))}
            </div>
          </div>
        )}

        <label className="tp-field-label" htmlFor="tp-language" style={{ marginTop: 16 }}>{t("storyCompose.languageLabel")}</label>
        <select
          id="tp-language"
          className="tp-input tp-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={busy}
        >
          {DEFAULT_LANGUAGES.map((l) => (
            <option key={l} value={l}>{t(`storyCompose.languageNames.${l}`)}</option>
          ))}
        </select>

        <label className="tp-field-label" style={{ marginTop: 16 }}>
          <Trans
            i18nKey="storyCompose.pagesLabel"
            components={{ strong: <strong /> }}
            values={{ count: pageCount }}
          />
        </label>
        <input
          type="range"
          min={MIN_PAGES}
          max={MAX_PAGES}
          step={1}
          value={pageCount}
          onChange={(e) => setPageCount(Number(e.target.value))}
          disabled={busy}
          style={{ width: "100%" }}
        />
        <div className="tp-eyebrow" style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--tp-ink-soft)" }}>
          <span>{MIN_PAGES}</span>
          <span>{MAX_PAGES}</span>
        </div>

        {credits && (
          credits.unlimited ? (
            <div
              className="tp-body"
              style={{
                marginTop: 24,
                padding: "12px 14px",
                borderRadius: 12,
                border: "2px solid var(--tp-ink)",
                background: "var(--tp-surface-alt)",
                fontSize: 13,
              }}
            >
              {t("credits.unlimited")}
            </div>
          ) : cantAfford ? (
            <div className="tp-error" style={{ marginTop: 24 }}>
              {t("credits.notEnough", { cost, balance })}
            </div>
          ) : (
            <div
              className="tp-body"
              style={{
                marginTop: 24,
                padding: "12px 14px",
                borderRadius: 12,
                border: "2px solid var(--tp-ink)",
                background: "var(--tp-surface-alt)",
                fontSize: 13,
              }}
            >
              <div>{t("credits.available", { balance })}</div>
              <div style={{ color: "var(--tp-ink-soft)", marginTop: 2 }}>
                {t("credits.bookCost", { cost, pages: pageCount, perPage: credits.perPage })}
              </div>
            </div>
          )
        )}

        {revisionOf ? (
          <>
            <button
              type="button"
              className="tp-btn tp-btn-primary tp-btn-block"
              disabled={!canSubmit}
              onClick={() => submit(true)}
              style={{ marginTop: 24 }}
            >
              {busy ? t("storyCompose.starting") : t("editBook.replaceOriginal")}
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-block"
              disabled={!canSubmit}
              onClick={() => submit(false)}
              style={{ marginTop: 8 }}
            >
              {busy ? t("storyCompose.starting") : t("editBook.saveAsNew")}
            </button>
            <div className="tp-body tp-soft" style={{ fontSize: 13, textAlign: "center", marginTop: 8 }}>{t("editBook.replaceHint")}</div>
          </>
        ) : (
          <button
            type="button"
            className="tp-btn tp-btn-primary tp-btn-block"
            onClick={() => submit(false)}
            disabled={!canSubmit}
            style={{ marginTop: 24 }}
          >
            {busy ? t("storyCompose.starting") : t("storyCompose.writeTheBook")}
          </button>
        )}

        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", fontSize: 12, marginTop: 14, textAlign: "center" }}>
          {t("storyCompose.emailNote")}
        </div>
      </div>

      <ProgressDialog
        open={busy}
        title={hasReferenceImages ? t("storyCompose.uploadingImages") : t("storyCompose.startingYourStory")}
        body={
          hasReferenceImages
            ? t("storyCompose.progressBodyWithImages")
            : t("storyCompose.progressBody")
        }
      />
    </div>
  );
}

function ReferenceSlot(props: {
  index: number;
  slot: RefSlot;
  disabled: boolean;
  onPick: (file: File | null) => void;
  onCaption: (caption: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const { index, slot, disabled, onPick, onCaption, onClear } = props;
  const inputId = `tp-ref-${index}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        htmlFor={inputId}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          aspectRatio: "1 / 1",
          borderRadius: 14,
          border: "2px dashed var(--tp-ink-soft)",
          background: slot.previewUrl ? `center / cover no-repeat url("${slot.previewUrl}")` : "transparent",
          color: "var(--tp-ink-soft)",
          fontSize: 13,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {!slot.previewUrl && <span>{t("storyCompose.addImage")}</span>}
        {slot.previewUrl && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (!disabled) onClear();
            }}
            disabled={disabled}
            aria-label={t("storyCompose.removeReferenceImage")}
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "1px solid var(--tp-ink)",
              background: "white",
              color: "var(--tp-ink)",
              fontSize: 16,
              lineHeight: "26px",
              cursor: "pointer",
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </label>
      <input
        id={inputId}
        type="file"
        accept={REF_MIME}
        disabled={disabled}
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        style={{ display: "none" }}
      />
      <input
        className="tp-input"
        type="text"
        maxLength={MAX_REF_CAPTION}
        placeholder={t("storyCompose.captionPlaceholder")}
        value={slot.caption}
        onChange={(e) => onCaption(e.target.value)}
        disabled={disabled || (!slot.file && !slot.stagedKey)}
        style={{ fontSize: 13, padding: "8px 10px" }}
      />
    </div>
  );
}
